import { useState } from "react";

import { cn } from "../libs/cn";
import type { Questionnaire, QuestionnaireAnswers, QuestionnaireField } from "../modules/states/tree";
import { Button } from "./ui/button";
import { Icon } from "./ui/icons";
import { Sheet } from "./ui/layout";

// Карточка анкеты «что спрашивал клиент» — как в прототипе: поля плиткой в две колонки, текст — на всю ширину.
// Та же карточка — форма подбора: без заголовка шаблона (title), он уже в заголовке экрана.
export function QuestionnaireCard({
  questionnaire,
  answers,
  index,
  title = true,
  onChange,
  onRemove,
}: {
  questionnaire: Questionnaire;
  answers: QuestionnaireAnswers;
  // Номер товара, когда анкет несколько; null — единственная.
  index: number | null;
  title?: boolean;
  onChange: (answers: QuestionnaireAnswers) => void;
  onRemove?: () => void;
}) {
  const set = (fieldId: string, answer: QuestionnaireAnswers[string]) => onChange({ ...answers, [fieldId]: answer });

  return (
    <section className="flex flex-col gap-3 rounded-2xl bg-[var(--color-surface)] p-4">
      <header className={cn("flex items-start justify-between gap-3", !title && !onRemove && "hidden")}>
        <h3 className="text-xs font-bold tracking-wider text-[var(--color-muted)] uppercase">
          {title && questionnaire.title}
          {index !== null && <span className="text-[var(--color-ink)]"> · товар {index}</span>}
        </h3>
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            aria-label={`Убрать товар ${index ?? ""}`}
            className="-mt-1 -mr-1 grid size-8 shrink-0 place-items-center rounded-full text-[var(--color-muted)] active:bg-black/5"
          >
            <Icon name="close" className="size-5" />
          </button>
        )}
      </header>
      <div className="grid grid-cols-2 gap-2">
        {questionnaire.fields.map((field) =>
          field.type === "select" ? (
            <SelectField
              key={field.id}
              field={field}
              optionId={answers[field.id]?.optionId}
              optionIds={answers[field.id]?.optionIds}
              onChange={(answer) => set(field.id, answer)}
            />
          ) : (
            <InputField
              key={field.id}
              field={field}
              value={answers[field.id]?.value ?? ""}
              onChange={(value) => set(field.id, { value })}
            />
          ),
        )}
      </div>
    </section>
  );
}

function FieldLabel({ field }: { field: QuestionnaireField }) {
  return (
    <span className="text-[11px] font-bold tracking-wider text-[var(--color-muted)] uppercase">
      {field.name}
      {field.required && <span className="text-[var(--color-danger)]"> *</span>}
    </span>
  );
}

// Длинные списки (виды изделий, камни) — с поиском; короткие — просто плитками строк.
const SEARCH_FROM = 8;

// Один вариант — тап закрывает шторку; у multiple варианты переключаются галочками, закрывает «Готово».
function SelectField({
  field,
  optionId,
  optionIds,
  onChange,
}: {
  field: QuestionnaireField;
  optionId: string | undefined;
  optionIds: string[] | undefined;
  onChange: (answer: { optionId?: string; optionIds?: string[] }) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const options = field.options ?? [];
  const chosenIds = field.multiple ? (optionIds ?? []) : optionId ? [optionId] : [];
  const chosen = options.filter((option) => chosenIds.includes(option.id));
  const current = chosen.length ? { name: chosen.map((option) => option.name).join(", ") } : undefined;
  const shown = query
    ? options.filter((option) => option.name.toLowerCase().includes(query.trim().toLowerCase()))
    : options;

  const close = () => {
    setOpen(false);
    setQuery("");
  };
  const pick = (id: string) => {
    if (!field.multiple) {
      onChange({ optionId: id });
      close();
      return;
    }
    const next = chosenIds.includes(id) ? chosenIds.filter((item) => item !== id) : [...chosenIds, id];
    onChange({ optionIds: next.length ? next : undefined });
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex min-h-16 flex-col items-start justify-center gap-0.5 rounded-xl bg-[var(--color-page)] px-3 py-2 text-left active:bg-[#ece8de]"
      >
        <FieldLabel field={field} />
        <span className="flex w-full items-center justify-between gap-2">
          <span className={cn("truncate font-semibold", !current && "font-normal text-[var(--color-muted)]")}>
            {current?.name ?? "Выберите"}
          </span>
          <Icon name="chevronDown" className="size-4 text-[var(--color-muted)]" />
        </span>
      </button>

      {open && (
        <Sheet onClose={close}>
          <h2 className="text-xl font-semibold">{field.name}</h2>
          {options.length > SEARCH_FROM && (
            <input
              autoFocus
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Поиск"
              aria-label={`Поиск: ${field.name}`}
              className="w-full rounded-xl bg-[var(--color-page)] px-4 py-3 text-base outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
            />
          )}
          <div className="-mx-2 flex max-h-[55dvh] flex-col overflow-y-auto">
            {shown.map((option) => {
              const selected = chosenIds.includes(option.id);
              return (
                <button
                  key={option.id}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => pick(option.id)}
                  className="flex min-h-12 items-center justify-between gap-3 rounded-xl px-3 text-left text-base active:bg-black/5"
                >
                  <span className={cn(selected && "font-semibold text-[var(--color-accent)]")}>{option.name}</span>
                  {selected && <Icon name="check" className="size-5 text-[var(--color-accent)]" />}
                </button>
              );
            })}
            {shown.length === 0 && <p className="px-3 py-4 text-[var(--color-muted)]">Ничего не нашлось</p>}
          </div>
          {field.multiple && (
            <Button size="md" onClick={close}>
              Готово{chosen.length ? ` · ${chosen.length}` : ""}
            </Button>
          )}
          {!field.required && current && (
            <button
              type="button"
              onClick={() => {
                onChange({});
                close();
              }}
              className="min-h-11 self-start rounded-xl px-2 text-sm font-semibold text-[var(--color-muted)] active:bg-black/5"
            >
              Очистить
            </button>
          )}
        </Sheet>
      )}
    </>
  );
}

function InputField({
  field,
  value,
  onChange,
}: {
  field: QuestionnaireField;
  value: string;
  onChange: (value: string) => void;
}) {
  const number = field.type === "number";
  return (
    <label
      className={cn(
        "flex min-h-16 flex-col justify-center gap-0.5 rounded-xl bg-[var(--color-page)] px-3 py-2 focus-within:ring-2 focus-within:ring-[var(--color-accent)]",
        !number && "col-span-2",
      )}
    >
      <FieldLabel field={field} />
      <span className="flex items-center gap-1">
        <input
          value={value}
          onChange={(event) => onChange(number ? event.target.value.replace(/[^\d\s]/g, "") : event.target.value)}
          inputMode={number ? "numeric" : "text"}
          placeholder={field.placeholder ?? (number ? "0" : "")}
          className="w-full min-w-0 bg-transparent font-semibold outline-none placeholder:font-normal placeholder:text-[var(--color-muted)]"
        />
        {field.unit && <span className="font-semibold text-[var(--color-muted)]">{field.unit}</span>}
      </span>
    </label>
  );
}
